import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { updateProgrammeFilters, updateSelectedFilters } from 'store/actions/filters'
import {
  ENPI_ENI_CROSS_BORDER,
  INTERREG_IPA_CROSS_BORDER,
  IPA_IPA_CROSS_BORDER_COOPERATION,
  OTHER_EUSBSR_RELATED,
  OTHER_EUSDR_RELATED,
  PERIOD_2000_2006,
  SELECTED_FILTER_MAX_LINES
} from 'store/constants/constants'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'

function checkDynamicOptions(currentOptions, selectedTypes) {
  let dynamicOptions = currentOptions
  if (
    selectedTypes.length > 0 &&
    selectedTypes.every(
      type =>
        type === ENPI_ENI_CROSS_BORDER ||
        type === IPA_IPA_CROSS_BORDER_COOPERATION ||
        type === INTERREG_IPA_CROSS_BORDER ||
        type === OTHER_EUSDR_RELATED ||
        type === OTHER_EUSBSR_RELATED
    )
  ) {
    dynamicOptions = dynamicOptions.filter(option => option.label !== PERIOD_2000_2006)
  }
  if (
    selectedTypes.length > 0 &&
    selectedTypes.every(type => type === OTHER_EUSDR_RELATED || type === OTHER_EUSBSR_RELATED)
  ) {
    dynamicOptions = []
  }
  return dynamicOptions
}

function ProgrammeRelatedMultiSelectPeriod({
  type,
  filterData,
  values,
  title,
  showFilter,
  keyWord,
  customTop,
  selection
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureProgrammeRelatedMultiSelectPeriod
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      showFilter={showFilter}
      keyWord={keyWord}
      customTop={customTop}
      filterObject={filterObject}
      selection={selection}
    />
  )
}

const PureProgrammeRelatedMultiSelectPeriod = React.memo(
  function PureProgrammeRelatedMultiSelectPeriod({
    type,
    filterData,
    values,
    title,
    showFilter,
    keyWord,
    customTop,
    filterObject,
    selection
  }) {
    const dispatch = useDispatch()

    const selectedValues = filterObject[values[0]]

    let options = filterData.map(option => {
      const parentOption = {
        label: option[keyWord],
        value: option.id,
        expanded: true
      }
      const selectedOption = selectedValues.filter(
        s => s.id === option.id && s.label === option[keyWord]
      )
      if (selectedOption.length > 0) {
        parentOption.checked = true
      }
      return parentOption
    })
    const { type: selectedTypes } = filterObject

    options =
      options.length > 0
        ? checkDynamicOptions(
            options,
            selectedTypes.map(selectedType => selectedType.label)
          )
        : []

    function setProgrammeFilters(selectedNodes) {
      const filterId = 'periods'
      const programmeFilterValue = selectedNodes.map(node => node.value || node.id).join(',')
      const programmeFilterData = {
        filterId,
        filterValue: programmeFilterValue
      }
      dispatch(updateProgrammeFilters(programmeFilterData))
    }

    const onChange = (currentNode, selectedNodes) => {
      const filterValue = selectedNodes.map(node => ({
        id: node.value,
        label: node.label
      }))
      const data = {
        filterType: type,
        filterId: values[0],
        filterValue
      }
      dispatch(updateSelectedFilters(data))
      setProgrammeFilters(selectedNodes)
      // Remove all selections from programmes filter since now the selected programmes might not be in available options
      const programmesData = {
        filterType: type,
        filterId: 'available',
        filterValue: []
      }
      dispatch(updateSelectedFilters(programmesData))
    }

    function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
      let resultingValue = null
      if (valueIsArray) {
        resultingValue = filterObject[filterId].filter(
          s => s.id !== filterValue.id || s !== filterValue
        )
      }
      const data = {
        filterType: type,
        filterId,
        filterValue: resultingValue
      }
      dispatch(updateSelectedFilters(data))
      setProgrammeFilters(resultingValue)
    }

    function renderSelectedValues() {
      return values.map(value => {
        if (filterObject[value]) {
          const filterValue = filterObject[value]
          if (filterValue instanceof Array) {
            return filterValue.map(v => (
              <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
                <SelectedFilter>
                  <FilterText title={v.label ? v.label : v}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon
                    className="far fa-times"
                    onClick={() => handleFilterRemove(value, v, true)}
                  />
                </SelectedFilter>
              </SelectedFilterContainer>
            ))
          }
        }
        return false
      })
    }

    return (
      <>
        {!selection ? (
          <SearchFilter open={showFilter} customTop={customTop}>
            <div className="sticky-container">
              {title ? <FilterTitle>{title}</FilterTitle> : null}
              <CustomDropDownTreeSelect data={options} onChange={onChange} />
            </div>
          </SearchFilter>
        ) : (
          <div className="selected-fl">{renderSelectedValues()}</div>
        )}
      </>
    )
  }
)

ProgrammeRelatedMultiSelectPeriod.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  selection: PropTypes.bool
}

PureProgrammeRelatedMultiSelectPeriod.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  selection: PropTypes.bool
}

export default ProgrammeRelatedMultiSelectPeriod
